import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/features/auth/AuthProvider";
import { queryKeys } from "@/shared/lib/queryKeys";
import { logger } from "@/shared/lib/logger";

/**
 * RealtimeProvider centralizes user-scoped postgres_changes subscriptions and
 * translates them into TanStack Query cache invalidations.
 *
 * Invalidations are intentionally narrow (no broad namespace busts) and
 * coalesced via a short debounce, so a burst of webhook-driven rows produces
 * a single refetch rather than a stream of flickers.
 */
const RealtimeContext = createContext<null>(null);

const DEBOUNCE_MS = 300;

export const RealtimeProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    // Tear down on logout / user switch.
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    // Clear pending debounced invalidations from the previous user.
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current.clear();

    if (!userId) return;

    const timers = timersRef.current;
    const schedule = (key: string, fn: () => void) => {
      const existing = timers.get(key);
      if (existing) clearTimeout(existing);
      timers.set(
        key,
        setTimeout(() => {
          timers.delete(key);
          fn();
        }, DEBOUNCE_MS),
      );
    };

    const channel = supabase
      .channel(`user-realtime-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "user_subscriptions",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          logger.log("[RealtimeProvider] user_subscriptions:", payload.eventType);
          schedule("subscription", () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.subscription.status(userId),
            });
            queryClient.invalidateQueries({
              queryKey: queryKeys.usage.chapters(userId),
            });
          });
        },
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "hunter_wallets",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          logger.log("[RealtimeProvider] hunter_wallets:", payload.eventType);
          schedule("wallet-mana", () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.wallet.mana(userId),
            });
          });
        },
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "ink_wallets",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          logger.log("[RealtimeProvider] ink_wallets:", payload.eventType);
          schedule("wallet-ink", () => {
            // What makes a Mollie purchase show up: the webhook credits the
            // wallet, this fires, and the balance chip refreshes while the
            // user is still on the Mollie redirect — no reload needed.
            queryClient.invalidateQueries({
              queryKey: queryKeys.wallet.ink(userId),
            });
          });
        },
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "mana_transactions",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          schedule("wallet-tx", () => {
            // Only invalidate the transaction list queries, not the entire
            // wallet namespace (which would refetch balance unnecessarily —
            // the hunter_wallets subscription already covers that).
            queryClient.invalidateQueries({
              queryKey: ["wallet", "manaTransactions", userId],
            });
          });
        },
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, [userId, queryClient]);

  return <RealtimeContext.Provider value={null}>{children}</RealtimeContext.Provider>;
};

// Exported so consumers can assert the provider is mounted (optional today).
export const useRealtime = () => useContext(RealtimeContext);
